import { HttpStatusCode } from "axios";
import { auth } from "config/firebase.config";
import { EPath } from "constants/path";
import { SecureStorageKeys } from "constants/secureStorage";
import {
  ErrorResponse,
  ErrorResponseFromJSON,
  Middleware,
  ResponseError,
} from "libs/cellera-sdk";

import { secureStorage } from "./secureStorage";

export const extractErrorMessage = async (
  response: Response
): Promise<string> => {
  try {
    const responseText = await response.clone().text();
    const errorResponse: ErrorResponse = ErrorResponseFromJSON(
      JSON.parse(responseText)
    );

    // Return the most specific error message available
    if (errorResponse?.messages && errorResponse?.messages?.length > 0) {
      return errorResponse?.messages?.join(", ");
    }
    if (errorResponse?.message) {
      return errorResponse.message;
    }
    if (errorResponse?.errorCode) {
      return `Error ${errorResponse?.errorCode}`;
    }
  } catch (parseError) {
    console.warn("Could not parse error response:", parseError);
  }

  return "An unknown error occurred";
};

const checkPlatformAuthorization = async (): Promise<boolean> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return false;
    }
    const idTokenResult = await currentUser.getIdTokenResult();
    return idTokenResult.claims.role === "provider";
  } catch (error) {
    console.error("Error checking platform authorization:", error);
    return false;
  }
};

/**
 * Check if the user requires onboarding by examining their custom claims
 * Returns true if user is authenticated and needs onboarding
 */
const checkIfRequiresOnboarding = async (): Promise<boolean> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      return false;
    }

    const idTokenResult = await currentUser.getIdTokenResult();
    return idTokenResult.claims.requiresOnboarding === true;
  } catch (error) {
    console.error("Error checking onboarding status:", error);
    return false;
  }
};

/**
 * Utility function to handle token expiration and redirect to login
 * This function can be called from anywhere in the app when token expiration is detected
 */
export const handleTokenExpiration = () => {
  console.warn(
    "Token expired or unauthorized access detected. Redirecting to login..."
  );

  // Clear the stored token
  secureStorage.remove(SecureStorageKeys.CUSTOM_TOKEN);

  // Redirect to login page
  // Use window.location.href to ensure a full page reload and clear any cached state
  window.location.href = EPath.LOGIN;
};

const handlePermissionError = async () => {
  const isPlatformAuthorized = await checkPlatformAuthorization();
  if (!isPlatformAuthorized) {
    throw new Error(
      "You’re using the wrong account type to sign in. Please try again with the correct account."
    );
  }

  console.warn("Permission error detected. Checking onboarding status...");

  const requiresOnboarding = await checkIfRequiresOnboarding();

  if (requiresOnboarding) {
    console.warn("User needs to complete onboarding. Redirecting...");
    window.location.href = EPath.ONBOARDING;
    return;
  }

  window.location.href = EPath.PROVIDER_DASHBOARD;
};

const isAuthError = (response: Response): boolean => {
  return response?.status === HttpStatusCode.Unauthorized;
};

const isPermissionError = (response: Response): boolean => {
  return response?.status === HttpStatusCode.Forbidden;
};

/**
 * Middleware to handle token expiration and redirect to login
 * This middleware intercepts API responses and checks for 401/403 errors
 * When token expires or permission is denied, it checks if user needs onboarding
 * and redirects accordingly (onboarding page vs login page)
 */
export const apiMiddleWare: Middleware = {
  post: async (context) => {
    const { response } = context;
    if (isAuthError(response)) {
      handleTokenExpiration();
      return;
    }
    if (isPermissionError(response)) {
      await handlePermissionError();
      return;
    }
    if (response?.ok === false) {
      const errorMessage = await extractErrorMessage(response);
      console.error("Error Message:", errorMessage);
      throw new ResponseError(response, errorMessage);
    }

    return response;
  },
};
